import React from "react";
import { type GetStaticPaths, type GetStaticProps, type NextPage } from "next";
import { groq } from "next-sanity";
import { serialize } from "next-mdx-remote/serialize";
import { MDXRemote, type MDXRemoteSerializeResult } from "next-mdx-remote";
import remarkGfm from "remark-gfm";
import rehypeSlug from "rehype-slug";
import rehypeHighlight from "rehype-highlight";
import GithubSlugger from "github-slugger";
import { unified } from "unified";
import remarkParse from "remark-parse";
import { visit } from "unist-util-visit";
import {
  getClient,
  getAnnouncementBanner,
  type Banner,
  getCdpComparisonPage,
  type CdpComparisonPage,
  type Customer,
  getDefaultLogos,
  type IntegrationSimple,
  getSimpleIntegrations,
  getFeaturedCustomers,
  getDefaultMobileLogos,
} from "lib/sanity";
import { components } from "components/mdx/components";
import { Page } from "components/page";
import { getPageMetaImage } from "utils";
import { ArticleSidebar, type SidebarHeading } from "components/sidebar";
import { Box } from "@chakra-ui/react";
import { ArticleJsonLd } from "next-seo";
import { horizontalContainerPadding } from "ui/container";
import { Article, ArticleContainer } from "components/article";
import { DemoEmbedFull } from "components/demo-embed";
import { CompareCdpsArticleHero } from "components/compare-cdps/compare-cdps-article-hero";
import { Section, sectionSpacingY } from "components/section";
import { StackedLogos } from "components/logos";
import { CustomerStats, IndustryLeaderAwards, SecurityFeatures, DestinationMarquee } from "components/content";
import { Rule } from "ui/rule";
import { Breadcrumbs } from "ui/breadcrumbs";

type PageProps = {
  announcementBanner?: Banner;
  page: CdpComparisonPage;
  defaultLogos: Customer[];
  defaultMobileLogos: Customer[];
  slug: string;
  mdx: MDXRemoteSerializeResult;
  headings: SidebarHeading[];
  destinations: IntegrationSimple[];
  featuredStatCustomers: Customer[];
};

const CompareCdpsPage: NextPage<PageProps> = ({
  announcementBanner,
  page,
  defaultLogos,
  defaultMobileLogos,
  slug,
  mdx,
  headings,
  destinations,
  featuredStatCustomers,
}) => {
  const pageTitle = `Compare ${page.firstCdpVendor.name} and ${page.secondCdpVendor.name}`;

  return (
    <Page
      hasSmoothScroll
      title={page.metaTitle ?? pageTitle}
      description={page.metaDescription ?? pageTitle}
      image={getPageMetaImage({ src: page.metaImageUrl, alt: page.metaTitle })}
      announcementBanner={announcementBanner}
    >
      <Section pos="relative" my={0} pt={{ base: 4, md: 6 }} overflow="hidden">
        <Breadcrumbs fontFamily="inter" crumbs={[{ title: "Compare more CDPs", href: "/compare-cdps" }]} mb="bigger" />

        <CompareCdpsArticleHero
          title={pageTitle}
          description={page.description}
          firstCdpVendor={page.firstCdpVendor}
          secondCdpVendor={page.secondCdpVendor}
        />

        <StackedLogos
          customers={defaultLogos}
          mobileCustomers={defaultMobileLogos}
          my={0}
          mt={{ base: "biggest", md: "huge" }}
        />
        <Rule py={{ base: "biggest", md: "huge" }} />
      </Section>

      <Section as="article" my={0}>
        <ArticleJsonLd
          type="Blog"
          url={`https://hightouch.com/compare-cdps/${page.slug}`}
          images={page.metaImageUrl ? [page.metaImageUrl] : ["https://hightouch.com/assets/meta.png"]}
          title={page.metaTitle ?? pageTitle}
          datePublished={page.createdAt}
          dateModified={page.updatedAt}
          authorName="Hightouch"
          description={page.metaDescription ?? pageTitle}
        />

        <ArticleContainer>
          <Article>
            <MDXRemote lazy components={components({ type: "cdpComparison" })} {...mdx} />
          </Article>

          <ArticleSidebar title="On this page" headings={headings} path={`compare-cdps/${slug}`} />
        </ArticleContainer>
      </Section>

      <CustomerStats customers={featuredStatCustomers} mb="0" pb={sectionSpacingY} />

      <Box overflow="hidden">
        <DemoEmbedFull hasBottomBorder pt={24} />
      </Box>

      <IndustryLeaderAwards hideBg my="0" py={sectionSpacingY} />

      <SecurityFeatures my="0" />

      <Rule maxW="container.section" mx="auto" my={{ base: "biggest", md: "huger" }} px={horizontalContainerPadding} />

      <DestinationMarquee destinations={destinations} my="0" pb={sectionSpacingY} />
    </Page>
  );
};

const serializeArticle = async (content: string) => {
  const mdx = await serialize(content, {
    mdxOptions: {
      rehypePlugins: [rehypeSlug, rehypeHighlight],
      remarkPlugins: [remarkGfm],
      format: "mdx",
    },
  });

  return mdx;
};

const getArticleHeadings = (blogContent: string): SidebarHeading[] => {
  const slugger = new GithubSlugger();
  const tree = unified().use(remarkParse).parse(blogContent);
  const headings: SidebarHeading[] = [];

  visit(tree, "heading", (node) => {
    if (node.depth === 2) {
      const title = blogContent
        .slice(node.position?.start.offset ?? 0, node.position?.end.offset ?? 0)
        .replace(/^#+/g, "")
        .trim();

      headings.push({
        title,
        href: `#${slugger.slug(title)}`,
      });
    }
  });

  return headings;
};

export const getStaticProps: GetStaticProps<PageProps> = async ({ params, preview = false }) => {
  const slug = params?.slug ? String(params?.slug) : "";
  const [page, announcementBanner, defaultLogos, defaultMobileLogos, destinations, featuredStatCustomers] =
    await Promise.all([
      getCdpComparisonPage(slug),
      getAnnouncementBanner(),
      getDefaultLogos(),
      getDefaultMobileLogos(),
      getSimpleIntegrations("destination"),
      getFeaturedCustomers(["petsmart", "minno", "lucidchart"]),
    ]);

  if (!page) {
    return {
      notFound: true,
    };
  }

  const mdx = await serializeArticle(page.content);

  return {
    props: {
      preview,
      slug,
      mdx,
      headings: getArticleHeadings(page.content),
      page,
      announcementBanner,
      defaultLogos,
      defaultMobileLogos,
      destinations,
      featuredStatCustomers,
    },
    revalidate: 60,
  };
};

export const getStaticPaths: GetStaticPaths = async () => {
  const slugs: Array<{ slug: string }> = await getClient(false).fetch(
    groq`*[_type == "cdpComparison"] { 'slug': slug.current }`,
  );

  return {
    paths: slugs.map(({ slug }: { slug: string }) => ({ params: { slug } })),
    fallback: "blocking",
  };
};

export default CompareCdpsPage;
